import { IconName } from '@/common/components/Icon/iconName';
import { ACCENT, NEUTRAL_LIGHT, QUATERNARY, TERTIARY } from '@/common/components/Label/Label';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { AccessTargetType } from '@/security/types/AccessTarget';
import { GlobalAccessLevelType } from '@/security/types/GlobalAccess';

export interface GlobalAccessChangeText {
  inherit: string;
  revoke: string;
}

export const PRODUCT_ACCESS_LEVELS: GlobalAccessLevelType[] = [
  {
    id: GlobalAccessLevel.Restricted,
    title: 'Restricted',
    description: 'Product is not available to non-company employees (except users with private access).',
  },
  {
    id: GlobalAccessLevel.Unlisted,
    title: 'Unlisted',
    description:
      'Anyone on the internet with DiLog or widget link can view product. Product is not visible in Cutwise Catalog.',
  },
  {
    id: GlobalAccessLevel.Public,
    title: 'Public',
    description: 'Product is available in Cutwise Catalog and via direct links (including widget \\ DiLog) to anyone.',
  },
];

export const PROJECT_ACCESS_LEVELS: GlobalAccessLevelType[] = [
  {
    id: GlobalAccessLevel.Restricted,
    title: 'Restricted',
    description: 'Project is not available to non-company employees (except users with private access).',
  },
  {
    id: GlobalAccessLevel.Public,
    title: 'Public',
    description: 'Project is available via direct link to anyone.',
  },
];

export const FOLDER_ACCESS_LEVELS: GlobalAccessLevelType[] = [
  {
    id: GlobalAccessLevel.Restricted,
    title: 'Private',
    description: 'Folder is available only to you.',
  },
  {
    id: GlobalAccessLevel.Public,
    title: 'Public',
    description: 'Folder is available via direct link to anyone.',
  },
];

export const COLLECTION_ACCESS_LEVELS: GlobalAccessLevelType[] = [
  {
    id: GlobalAccessLevel.Restricted,
    title: 'Private',
    description: 'Collection is available only to you.',
  },
  {
    id: GlobalAccessLevel.Public,
    title: 'Public',
    description: 'Collection is available via direct link to anyone.',
  },
];

export const DRIVE_PROJECT_ACCESS_CHANGE_TEXT: GlobalAccessChangeText = {
  inherit: 'Share all products and projects inside the multistage project with everyone',
  revoke: 'This action will also remove access to the nested projects and products for external users',
};

export const DRIVE_FOLDER_ACCESS_CHANGE_TEXT: GlobalAccessChangeText = {
  inherit: 'Make your projects and products inside the folder public',
  revoke: 'This action will not change the visibility of nested projects and products.',
};

export const COLLECTION_ACCESS_CHANGE_TEXT: GlobalAccessChangeText = {
  inherit: 'Make your products inside the collection public',
  revoke: 'This action will not change the visibility of the products inside the collection.',
};

export function getAccessByType(type: AccessTargetType) {
  if (type === 'product') {
    return PRODUCT_ACCESS_LEVELS;
  }

  if (type === 'driveFolder') {
    return FOLDER_ACCESS_LEVELS;
  }

  if (type === 'collection') {
    return COLLECTION_ACCESS_LEVELS;
  }

  if (type === 'project' || type === 'driveProject') {
    return PROJECT_ACCESS_LEVELS;
  }

  return [];
}

export function getAccessById(id: GlobalAccessLevel, type: AccessTargetType) {
  return getAccessByType(type).find((access) => access.id === id);
}

const ACCESS_APPEARANCE = {
  [GlobalAccessLevel.Restricted]: TERTIARY,
  [GlobalAccessLevel.Unlisted]: QUATERNARY,
  [GlobalAccessLevel.Public]: ACCENT,
} as const;

const ACCESS_ICON: Record<GlobalAccessLevel, IconName> = {
  [GlobalAccessLevel.Restricted]: 'block',
  [GlobalAccessLevel.Unlisted]: 'code',
  [GlobalAccessLevel.Public]: 'check',
};

export function getAccessLabelAppearance(level: GlobalAccessLevel) {
  return ACCESS_APPEARANCE[level];
}

export function getDriveAccessAppearance(level: GlobalAccessLevel) {
  return level === GlobalAccessLevel.Restricted ? NEUTRAL_LIGHT : ACCENT;
}

export function getAccessLabelIcon(level: GlobalAccessLevel) {
  return ACCESS_ICON[level];
}
